import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

// Visualisation palettes for SVG / canvas surfaces that cannot read Tailwind tokens.
export const VIZ = {
  dark: {
    cyan: ["#6EA8C7", "#3E6A82"],
    emerald: ["#5E9E7E", "#3B6B54"],
    amber: ["#C99A4B", "#8A6A2E"],
    red: ["#CF6E66", "#7E3A34"],
    purple: ["#8A7FA8", "#5B5273"],
    orange: "#B5744A",
    ink: "#E2E8F0",
    inkDim: "rgba(203,213,225,0.66)",
    bevelHi: "rgba(255,255,255,0.22)",
    bevelMid: "rgba(255,255,255,0.02)",
    bevelLo: "rgba(0,0,0,0.55)",
    coreHi: "rgba(255,255,255,0.07)",
    ringTrack: "rgba(255,255,255,0.055)",
    ringInner: "rgba(0,0,0,0.6)",
    ocean: ["#07121A", "#03060A"],
    sphere: "rgba(110,168,199,0.14)",
    graticule: "rgba(110,168,199,0.05)",
    landFill: "rgba(120,148,178,0.10)",
    landStroke: "rgba(110,168,199,0.22)",
    arcHalo: "rgba(207,110,102,0.18)",
    arcFrom: "rgba(207,110,102,0)",
    arcMid: "rgba(207,110,102,0.95)",
    arcTo: "rgba(201,154,75,0.95)",
    keyTop: ["#2B3444", "#161C26", "#0A0D13"],
    keySide: ["#0B0E14", "#04060A"],
    keyMetal: ["#DFE7F3", "#8D9BB2", "#5B697F", "#C3CDDD"],
    keySheen: ["rgba(255,255,255,0.20)", "rgba(255,255,255,0.02)", "rgba(255,255,255,0)"],
    keyEdge: "rgba(255,255,255,0.14)",
    keyEtch: "rgba(255,255,255,0.34)",
    keyRing: "rgba(255,255,255,0.10)",
    keyHole: "#04060A",
    keyShadow: "rgba(110,168,199,0.16)",
    ledIdle: "#334155",
    clusterStroke: "rgba(203,213,225,0.4)",
  },
  light: {
    cyan: ["#2F6E8F", "#7BAAC4"],
    emerald: ["#2C7355", "#7DB197"],
    amber: ["#8A6520", "#CBA968"],
    red: ["#A8332C", "#D28C86"],
    purple: ["#59497A", "#A196B8"],
    orange: "#8A4E22",
    ink: "#0F172A",
    inkDim: "rgba(15,23,42,0.62)",
    bevelHi: "rgba(15,23,42,0.14)",
    bevelMid: "rgba(15,23,42,0.03)",
    bevelLo: "rgba(15,23,42,0.22)",
    coreHi: "rgba(15,23,42,0.04)",
    ringTrack: "rgba(15,23,42,0.10)",
    ringInner: "rgba(15,23,42,0.12)",
    ocean: ["#E9EFF4", "#DBE3EB"],
    sphere: "rgba(47,110,143,0.30)",
    graticule: "rgba(47,110,143,0.10)",
    landFill: "rgba(47,110,143,0.14)",
    landStroke: "rgba(47,110,143,0.38)",
    arcHalo: "rgba(168,51,44,0.16)",
    arcFrom: "rgba(168,51,44,0)",
    arcMid: "rgba(168,51,44,0.95)",
    arcTo: "rgba(138,101,32,0.95)",
    keyTop: ["#F4F7FA", "#DFE5EE", "#C2CAD6"],
    keySide: ["#B7C0CD", "#98A3B2"],
    keyMetal: ["#FFFFFF", "#C9D2DE", "#93A0B1", "#E3E9F1"],
    keySheen: ["rgba(255,255,255,0.6)", "rgba(255,255,255,0.12)", "rgba(255,255,255,0)"],
    keyEdge: "rgba(15,23,42,0.18)",
    keyEtch: "rgba(15,23,42,0.45)",
    keyRing: "rgba(15,23,42,0.14)",
    keyHole: "#AAB4C2",
    keyShadow: "rgba(47,110,143,0.18)",
    ledIdle: "#94A3B8",
    clusterStroke: "rgba(15,23,42,0.35)",
  },
};

export const CLUSTER_TONE = {
  CONFIRMED_MALICIOUS: "red",
  SUSPICIOUS: "amber",
  BENIGN: "emerald",
  INDETERMINATE: "purple",
  CUSTODY: "cyan",
};

const Ctx = createContext({ theme: "dark", printing: false, viz: VIZ.dark, toggle: () => {} });

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => window.localStorage.getItem("aegis.theme") || "dark");
  const [printing, setPrinting] = useState(false);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
  }, [theme]);

  // Printing forces the day palette in CSS; mirror that for the JS-driven SVG/canvas colours.
  useEffect(() => {
    const mq = window.matchMedia("print");
    const sync = (e) => setPrinting(e.matches);
    setPrinting(mq.matches);
    const before = () => setPrinting(true);
    const after = () => setPrinting(false);
    mq.addEventListener("change", sync);
    window.addEventListener("beforeprint", before);
    window.addEventListener("afterprint", after);
    return () => {
      mq.removeEventListener("change", sync);
      window.removeEventListener("beforeprint", before);
      window.removeEventListener("afterprint", after);
    };
  }, []);

  const toggle = useCallback(() => {
    setTheme((t) => {
      const next = t === "dark" ? "light" : "dark";
      window.localStorage.setItem("aegis.theme", next);
      return next;
    });
  }, []);

  const value = useMemo(
    () => ({ theme, printing, viz: VIZ[printing ? "light" : theme] || VIZ.dark, toggle }),
    [theme, printing, toggle]
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useTheme = () => useContext(Ctx);
